import axios, {AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import {datadogLogs} from "@datadog/browser-logs";
import {v4 as uuidv4} from 'uuid';
import {retrieveLaunchParams} from "@telegram-apps/sdk-react";
import ct from 'countries-and-timezones'

const BASE_URL = import.meta.env.VITE_API_HOST;
const TIMEOUT = 30000;

const getAuthToken = (): string | null => {
  function base64encode(str: string): string {
    const encode = encodeURIComponent(str).replace(/%([a-f0-9]{2})/gi, (_m, $1) => String.fromCharCode(parseInt($1, 16)))
    return btoa(encode)
  }
  const lp = retrieveLaunchParams();
  return base64encode(lp.initDataRaw ? lp.initDataRaw : '');
};

const getStartParam = (): string => {
  const lp = retrieveLaunchParams();
  return lp.initData?.startParam || "";
}

const logError = (error: AxiosError) => {
  datadogLogs.logger.error("API Error:", {
    message: error.message,
    url: error.config?.url,
    method: error.config?.method,
    requestBody: error.config?.data,
    status: error.response?.status,
    response: error.response?.data,
  }, error);
};

const retryRequest = async (error: AxiosError, retries: number = 3): Promise<AxiosResponse | null> => {
  if (!error.config) {
    throw error;
  }
  let attempt = 0;
  while (attempt < retries) {
    attempt += 1;
    try {
      datadogLogs.logger.warn(`Retry attempt ${attempt}`, {url: error.config.url});
      return await axios.request(error.config);
    } catch (retryError) {
      if (attempt >= retries) {
        if (retryError instanceof AxiosError) {
          logError(retryError);
        }
        throw retryError;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
  return null;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.set("Authorization", token);
      config.headers.set("X-Tg-Start-Param", getStartParam());
    }
    const systemOptions = new Intl.DateTimeFormat().resolvedOptions();
    config.headers.set("X-Client-Timezone", systemOptions.timeZone);
    const countries = ct.getTimezone(systemOptions.timeZone)?.countries
    let country = ""
    if (countries && countries.length > 0) {
      country = countries[0];
    }
    config.headers.set("X-Client-Country", country);
    config.headers.set("X-Request-ID", uuidv4());
    return config;
  },
  (error) => {
    logError(error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    if (!error.response || error.response.status >= 500) {
      return retryRequest(error, 3);
    } else {
      logError(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;